/*
 * The preorder guard: no checkout from a cart that mixes a preorder dish with
 * this week's meals, while a preorder window is open. MAV-889, from Tim's ask
 * in #monet on 2026-10-10. Loaded on every page by
 * snippets/maven-preorder-guard.liquid.
 *
 * WHY IT EXISTS. Kumquat schedules a whole order into ONE delivery week. When
 * any line carries the open window's preorder tag, every line goes to the
 * preorder week, and the Carrier Service quotes that week too, so checkout and
 * the order agree and nothing shows an error (docs/theme-contracts.md §1, "An
 * Add control cannot ship before the client"). The old Debut theme stopped a
 * mixed cart in the browser. This is that rule on Dawn.
 *
 * THE CONFIG is `shop.metafields.maven_meals_app.preorderConfig`, which each
 * save in Kumquat Settings → Rates & Scheduling writes. It is an array:
 *   [{ tag: "Holiday", mixed: false,
 *      dateRange: { start: "2025-12-01T08:00:00.000Z", end: "…" }, … }]
 * The snippet prints it into <meta name="preorder-config">.
 *
 * THE RULES, copied from the old theme (assets/bentosmb-custom.js.liquid on
 * `trial`, `activePreorder()` and `checkMixedCartOnLoad()`), so a cart is
 * judged the way it was judged before:
 *  - A window is open from 02:00 on its start day to 01:59:59.999 on the day
 *    after its end day, in the browser's own time zone.
 *  - The first open window counts. With `mixed: true` it allows a mixed
 *    cart, and the guard does nothing.
 *  - A line is a preorder when its product carries the tag
 *    `preorder-<window tag>`, compared without case.
 *  - The tip product (`tip-product`) is neither kind.
 *
 * WHAT IT DOES. A checkout attempt, from any button or link that starts one,
 * waits while the guard reads the cart. A mixed cart shows a dialog: keep this
 * week's meals or keep the preorder items. The choice removes the other group
 * and opens the cart. Nothing is removed without that choice. On /cart the
 * dialog also opens on arrival, so the customer sees the problem before the
 * button.
 *
 * ⚠️ THE CART'S EXPRESS BUTTONS (Shop Pay and others) ARE HIDDEN, NOT
 * GUARDED. A click inside them cannot be intercepted, so during a window the
 * snippet hides them until this script finds the cart is not mixed.
 *
 * ⚠️ A MEAL BUNDLE IS JUDGED LINE BY LINE, BY HEIDI'S DECISION OF 2026-10-11.
 * A pre-order entrée added through the "Choose sides" tray with sides from
 * this week's menu makes a mixed cart, and the dialog splits the meal. The
 * review of promotion #608 asked whether a paired side should count as part
 * of the pre-order instead. Heidi chose to keep this rule: Kumquat sends a
 * whole order to one week, so those sides would arrive in the pre-order week,
 * and the kitchen does not cook this week's sides for that week. The way to
 * avoid a split meal is in Kumquat's Bundles page: do not pair a pre-order
 * entrée with weekly sides. Do not add bundle awareness here without a new
 * decision from her.
 *
 * ⚠️ IT DOES NOTHING OUTSIDE AN OPEN, NON-MIXED WINDOW. No request, no
 * listener work, no dialog. So the guard costs nothing for 48 weeks a year.
 *
 * ⚠️ A FAILED CHECK LETS CHECKOUT THROUGH. If the cart or a product cannot be
 * read, the guard does not trap a customer with a working cart. It logs a
 * warning. The cart is then as unguarded as it was before this file.
 *
 * Tags come from /products/<handle>.js, one request for each product in the
 * cart, cached for the page, because /cart.js carries no tags.
 */
(function () {
  'use strict';

  var TIP_HANDLE = 'tip-product';

  function readConfig(doc) {
    var meta = doc && doc.querySelector ? doc.querySelector('meta[name="preorder-config"]') : null;
    var raw = meta ? meta.getAttribute('content') : '';
    if (!raw || raw === 'null' || raw === '""') return [];
    try {
      var parsed = JSON.parse(raw);
      /* A `json_string` metafield can arrive as a string that itself holds
       * the JSON. Parse a second time in that case. */
      if (typeof parsed === 'string') parsed = JSON.parse(parsed);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }

  /* ⚠️ A DATE WITH NO TIME IS A CALENDAR DAY, READ IN LOCAL TIME. Kumquat
   * wrote ISO timestamps at midnight Pacific until 2025 ("…T08:00:00.000Z").
   * The 2026 Thanksgiving window, saved on 2026-10-11, has plain days
   * ("2026-10-28"). `new Date("2026-10-28")` is midnight UTC, which is 5 PM
   * the evening before in Seattle, so the window would have opened a day
   * early and closed a day early. A plain day is built from its parts. */
  function parseDay(value) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return new Date(value);
  }

  /* The old theme's window, exactly: start day at 02:00, to the day after
   * the end day at 01:59:59.999, local time. */
  function activeWindow(config, now) {
    var curr = now || new Date();
    for (var i = 0; i < (config || []).length; i += 1) {
      var w = config[i] || {};
      var range = w.dateRange || {};
      if (!w.tag || !range.start || !range.end) continue;
      var start = parseDay(range.start);
      start.setHours(2, 0, 0, 0);
      var end = parseDay(range.end);
      end.setDate(end.getDate() + 1);
      end.setHours(1, 59, 59, 999);
      if (isNaN(start) || isNaN(end)) continue;
      if (start <= curr && end >= curr) {
        return { tag: String(w.tag), mixed: w.mixed === true };
      }
    }
    return null;
  }

  function isPreorder(tags, windowTag) {
    var want = ('preorder-' + windowTag).toLowerCase();
    return (tags || []).some(function (t) {
      return String(t).toLowerCase() === want;
    });
  }

  /* Splits the cart's lines into the two groups. `tagsByHandle` maps a
   * product handle to its tag array. */
  function classify(items, tagsByHandle, windowTag) {
    var out = { preorder: [], regular: [] };
    (items || []).forEach(function (item) {
      if (!item || String(item.handle || '').toLowerCase() === TIP_HANDLE) return;
      var tags = (tagsByHandle && tagsByHandle[item.handle]) || [];
      (isPreorder(tags, windowTag) ? out.preorder : out.regular).push(item);
    });
    return out;
  }

  /* The /cart/update.js body that keeps one group and removes the other. */
  function removalUpdates(groups, keep) {
    var drop = keep === 'preorder' ? groups.regular : groups.preorder;
    var updates = {};
    drop.forEach(function (item) {
      updates[item.key] = 0;
    });
    return updates;
  }

  /* Exported for scripts/preorder-guard.test.mjs. */
  if (typeof window !== 'undefined') {
    window.MavenPreorderGuard = {
      readConfig: readConfig, parseDay: parseDay, activeWindow: activeWindow, isPreorder: isPreorder,
      classify: classify, removalUpdates: removalUpdates
    };
  }

  if (typeof document === 'undefined' || !document.addEventListener || !window.fetch) return;

  var win = activeWindow(readConfig(document));
  if (!win || win.mixed) return;

  /* The cart's express buttons stay hidden (class `mv-preorder-hold`, set
   * by the snippet's inline script) until a check finds the cart is not
   * mixed. A failed check shows them too, by the same rule as the button. */
  function release() {
    document.documentElement.classList.remove('mv-preorder-hold');
  }

  var tagCache = {};
  function tagsFor(handle) {
    if (tagCache[handle]) return tagCache[handle];
    tagCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('product ' + r.status);
        return r.json();
      })
      .then(function (p) {
        return Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',').map(function (t) { return t.trim(); });
      })
      .catch(function (err) {
        delete tagCache[handle];
        throw err;
      });
    return tagCache[handle];
  }

  /* Resolves to the two groups when the cart is mixed, and to null when it
   * is not. Rejects when the cart or a product cannot be read. */
  function checkCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('cart ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        var items = cart.items || [];
        var handles = [];
        items.forEach(function (i) {
          if (i.handle && handles.indexOf(i.handle) === -1 && i.handle.toLowerCase() !== TIP_HANDLE) handles.push(i.handle);
        });
        return Promise.all(handles.map(tagsFor)).then(function (lists) {
          var byHandle = {};
          handles.forEach(function (h, n) { byHandle[h] = lists[n]; });
          var groups = classify(items, byHandle, win.tag);
          return groups.preorder.length && groups.regular.length ? groups : null;
        });
      });
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function copy(name) {
    var meta = document.querySelector('meta[name="preorder-config"]');
    return (meta && meta.getAttribute('data-' + name)) || '';
  }

  var dialog = null;
  function showDialog(groups) {
    if (dialog && dialog.open) return;
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'mv-preorder-guard';
      dialog.setAttribute('aria-labelledby', 'mv-preorder-guard-title');
      document.body.appendChild(dialog);
    }
    var label = esc(win.tag);
    dialog.innerHTML =
      '<h2 class="f-h3 mv-preorder-guard__title" id="mv-preorder-guard-title">' + esc(copy('title')) + '</h2>' +
      '<p class="mv-preorder-guard__body">' + esc(copy('body')).split('{WINDOW}').join(label) + '</p>' +
      '<div class="mv-preorder-guard__actions">' +
        '<button type="button" class="btn btn-primary" data-keep="regular">' + esc(copy('keep-weekly')) + '</button>' +
        '<button type="button" class="btn btn-ghost" data-keep="preorder">' + esc(copy('keep-preorder')).split('{WINDOW}').join(label) + '</button>' +
        '<button type="button" class="mv-preorder-guard__cancel" data-keep="">' + esc(copy('cancel')) + '</button>' +
      '</div>';
    dialog.onclick = function (e) {
      var b = e.target.closest ? e.target.closest('button[data-keep]') : null;
      if (!b) return;
      var keep = b.getAttribute('data-keep');
      if (!keep) { dialog.close(); return; }
      [].forEach.call(dialog.querySelectorAll('button'), function (x) { x.disabled = true; });
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: removalUpdates(groups, keep) })
      })
        .then(function () { window.location.href = '/cart'; })
        .catch(function () { window.location.href = '/cart'; });
    };
    if (dialog.showModal) dialog.showModal();
    else dialog.setAttribute('open', '');
  }

  /* A checkout starts from a button or input named `checkout` (the cart page,
   * Dawn's cart popup and drawer) or from a link to /checkout. */
  function checkoutTrigger(el) {
    if (!el || !el.closest) return null;
    return el.closest('[name="checkout"], a[href="/checkout"], a[href^="/checkout?"], a[href^="/checkouts/"]');
  }

  var passing = null;
  document.addEventListener('click', function (e) {
    var t = checkoutTrigger(e.target);
    if (!t) return;
    if (passing === t) { passing = null; return; }
    e.preventDefault();
    e.stopImmediatePropagation();
    checkCart().then(function (groups) {
      if (groups) { showDialog(groups); return; }
      release();
      passing = t;
      t.click();
    }, function (err) {
      console.warn('[maven-preorder-guard] could not read the cart, checkout continues:', err);
      passing = t;
      t.click();
    });
  }, true);

  /* On every page: a page with no express buttons is released at once, with
   * no request. A page that has them (the cart page, a cart drawer) checks the
   * cart and releases them when it is not mixed, or when the check fails.
   * ⚠️ The review of promotion #608 found that the first version released
   * only on /cart, so a cart drawer on any other page kept them hidden for
   * the whole window. On /cart a mixed cart also opens the dialog. */
  var EXPRESS = '.cart__dynamic-checkout-buttons, .additional-checkout-buttons, shopify-accelerated-checkout-cart';
  var onCart = /^\/cart\/?$/.test(window.location.pathname);
  var run = function () {
    if (!onCart && !document.querySelector(EXPRESS)) { release(); return; }
    checkCart().then(function (groups) {
      if (!groups) { release(); return; }
      if (onCart) showDialog(groups);
    }, function () { release(); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
